export type DuplicateAudioPublication = {
  participantIdentity: string | null;
  publication: any;
  trackSid: string | null;
};

export type DuplicateAudioGuardResult = {
  activeCount: number;
  kept: DuplicateAudioPublication | null;
  duplicates: DuplicateAudioPublication[];
};

export type DuplicateAudioGuardOptions = {
  preferSpatiusAvatarAudio?: boolean;
};

function isAudioKind(kind: unknown) {
  return kind === "audio";
}

function isSpatiusAvatarIdentity(identity: string | null | undefined) {
  return Boolean(identity?.startsWith("spatius-avatar"));
}

export function getRemoteAudioPublicationKey(
  participantIdentity: string | null | undefined,
  publication: any
) {
  const trackSid =
    publication?.trackSid ||
    publication?.sid ||
    publication?.track?.sid ||
    publication?.track?.trackSid ||
    null;

  return `${participantIdentity || "unknown"}:${trackSid || "unknown"}`;
}

function isPublicationSubscribed(publication: any) {
  return Boolean(publication?.isSubscribed ?? publication?.subscribed ?? publication?.track);
}

function getPublicationCollections(participant: any) {
  const audioPublications = participant?.audioTrackPublications;
  if (audioPublications?.values && audioPublications.size > 0) {
    return audioPublications.values();
  }

  return participant?.trackPublications?.values?.() ?? [];
}

export function collectActiveRemoteAudioPublications(room: any): DuplicateAudioPublication[] {
  const publications: DuplicateAudioPublication[] = [];

  for (const participant of room?.remoteParticipants?.values?.() ?? []) {
    for (const publication of getPublicationCollections(participant)) {
      const kind = publication?.kind ?? publication?.track?.kind;
      if (!isAudioKind(kind) || !isPublicationSubscribed(publication)) {
        continue;
      }

      publications.push({
        participantIdentity: participant?.identity || null,
        publication,
        trackSid:
          publication?.trackSid ||
          publication?.sid ||
          publication?.track?.sid ||
          publication?.track?.trackSid ||
          null,
      });
    }
  }

  return publications;
}

function chooseAudioOwner(
  publications: DuplicateAudioPublication[],
  options: DuplicateAudioGuardOptions
) {
  const preferred = options.preferSpatiusAvatarAudio
    ? publications.filter((publication) => isSpatiusAvatarIdentity(publication.participantIdentity))
    : publications.filter(
        (publication) => !isSpatiusAvatarIdentity(publication.participantIdentity)
      );

  const candidates = preferred.length > 0 ? preferred : publications;
  return candidates[candidates.length - 1] ?? null;
}

export function inspectDuplicateRemoteAudioPublications(
  room: any,
  options: DuplicateAudioGuardOptions = {}
): DuplicateAudioGuardResult {
  const activePublications = collectActiveRemoteAudioPublications(room);
  const kept = activePublications.length > 1 ? chooseAudioOwner(activePublications, options) : null;

  if (!kept) {
    return {
      activeCount: activePublications.length,
      kept: activePublications[0] ?? null,
      duplicates: [],
    };
  }

  return {
    activeCount: activePublications.length,
    kept,
    duplicates: activePublications.filter((publication) => publication !== kept),
  };
}
